// Home, as the headset draws it (docs/features/home-mac-tabs.md over home-redesign.md; HomeView.swift, HomeList.swift).
// The title and the toolbar share a row: Input Tools, Widgets, Mac Windows, Files on a Mac, then ⋯ for the rest.
// Below, a **tab per paired Mac** (its name, a state dot — on / connecting / off / signed out / paused — and a Needs
// you count; paused and off tabs dimmed; the last one picked is remembered; one Mac shows just its name). Under the
// tab, that Mac only:
//   its toolbar — search (titles, folders, first prompts), Sort (Recent · Needs you · Cost), Select, and the Mac's ⋯
//     (Camera…, Update Mac App…, Usage…, Robots…, Links Open On, Pause / Resume, Forget This Mac);
//   filter chips — kind (opencode, each extension) and status (Working, Waiting on you,
//     Live); chips of one sort are any-of, of different sorts all-of;
//   its state line (core/macstate.js: headless · lid closed · locked · 56% · held until unlock), then its notices
//     (offline, the relay card, off by its schedule, paused with Resume);
//   the orange Needs you strip, Pinned, then the lists by kind — Sessions, then each extension's rows
//     (extensions.js) — and Background (its widgets and saved panes) last, folded.
// A row: status dot, title, "folder · when", kind badge, cost. Tap opens; a long press is its menu (Open, Pin /
// Unpin, Select and Delete… for opencode, an extension's own actions). An opencode session an extension speaks for
// shows only as that extension's row.
//
// The open tab is the Mac (docs/features/home-mac-tabs.md › The tab is the Mac — Josh 10-16: "have the Mac tabs switch context for
// everything to that Mac"): picking a tab makes it the app's current Mac (`actions.chooseMac`), New Session reads
// "New Session ‹● Mac›" and opens there, and every Mac-scoped item in More (MAC_SCOPED) opens for it. In Columns —
// every Mac at once — or on a paused Mac's tab they ask which Mac first (a short menu, the last used first); a Mac
// picked anywhere else (a window's own picker, a Mac's ⋯) moves the tab with it (`follow`).
//
// One seam: `new HomeWindow(actions)` → `root` (the DOM the scene paints), `render(state)` and `follow(key)`.
import { icon, onIcons, addSymbols } from '../../core/icons.js'
import { homeItems } from '../../core/home-items.js'
import { relayCard } from '../../core/relaycard.js'
import { phoneBridge, phoneAddMac } from '../../core/phonekey.js'
import { chipText, detailLines } from '../../core/relaystatus.js'
import { stateLine } from '../../core/macstate.js'
import { mcpCards } from './mcpcards.js'
import { routeIcon } from '../../core/routeicons.js'
import { scheduleCard } from '../../core/schedulecard.js'
import { problemCard } from '../../core/problemcard.js'
import { macColor } from '../../tiny-engineer/maccolors.js'
import { useStyles } from '../markdown/markdown.js'
import { newOffers } from '../widgets/offers.js'
import { claudeSections, archivedApart } from './claude-groups.js'
import { archivedButton, archivedSheet, archivedTap } from './archived.js'
import { claudeWaits, isClaudeRow } from './claude-waits.js'
import { moreGrid, macGrid, bumpUse, gridKind, gridStyles } from './grid.js'
import { orderRecent } from './order.js'
import { displayTitle } from '../../core/session-title.js'
import { needsStrip, needsTap, untilToggle, untilSend } from './needs.js'
import { tickCountdowns } from './countdown.js'
import { onAskChange } from '../../claudeask/answer.js'

const stylesReady = useStyles('windows/home/home.css')
addSymbols({ 'rectangle.stack': 'layers', 'rectangle.split.3x1': 'columns-3', 'checkmark.circle': 'circle-check', bell: 'bell' })

const esc = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]))
// a worktree as "repo · name" (its name is what tells two apart at a phone's width — the path's end is cut), else ~/…
const tilde = (dir) => { const d = dir || '', at = d.indexOf('/.claude/worktrees/'); return at >= 0 ? (d.slice(0, at).split('/').pop() || '~') + ' · ' + (d.slice(at + 19).split('/')[0] || '') : d.replace(/^\/Users\/[^/]+/, '~') || '~' }
/// The route popover's relay part (core/relaystatus.js): what the Mac measured, and why direct is free.
function relayDetail(m, line) {
  const d = detailLines(m.relayStatus, m.name)
  return d.lines.map(([a, b]) => line(a, b)).join('') + `<div class="rnote">${esc(d.note)}</div>`
}

function ago(ms) {
  if (!ms) return ''
  const s = Math.max(0, (Date.now() - ms) / 1000)
  if (s < 60) return 'now'
  if (s < 3600) { const m = Math.round(s / 60); return m + (m === 1 ? ' minute ago' : ' minutes ago') }
  if (s < 86400) { const h = Math.round(s / 3600); return h + (h === 1 ? ' hour ago' : ' hours ago') }
  if (s < 172800) return 'yesterday'
  return Math.round(s / 86400) + ' days ago'
}
const money = (v) => (v == null ? '' : v === 0 ? 'covered' : v < 0.01 ? '<$0.01' : '$' + (v < 100 ? v.toFixed(2) : Math.round(v)))
const store = { get(k, f) { try { const v = localStorage.getItem(k); return v == null ? f : JSON.parse(v) } catch { return f } }, set(k, v) { try { localStorage.setItem(k, JSON.stringify(v)) } catch {} } }

/// A labeled control along Home's top (Josh, 09-29: "so many icons on the top it can be hard to know what they do
/// without labels"): its icon, its name beside it (icons only on a phone-width screen), the name as title and aria-label.
const labeled = (act, label, symbol, { cls = '', disabled = false, title = label, pill = 0 } = {}) =>
  `<button class="ht-lb${cls ? ' ' + cls : ''}" data-act="${esc(act)}" title="${esc(title)}" aria-label="${esc(title)}" ${disabled ? 'disabled' : ''}><img src="${icon(symbol)}"><span class="ht-lbl">${esc(label)}</span>${pill ? `<span class="ht-pill">${pill}</span>` : ''}</button>`
const FIRST_ROWS = 6   // a list shows its first six, then "Show all N"
const STATUS_CHIPS = [['working', 'Working'], ['waiting', 'Waiting on you'], ['live', 'Live']]
const SORTS = [['recent', 'Recent'], ['needs', 'Needs you'], ['cost', 'Cost']]
const DOT = { online: 'var(--c-green)', connecting: 'var(--c-yellow)', offline: 'var(--c-orange)', unauthorized: 'var(--c-red)', paused: 'rgb(var(--c-ink-rgb) / 0.4)' }
const PINS = 'homePins.v1', TAB = 'home.tab', LAYOUT = 'home.layout'
const COL_W = 420, COL_GAP = 12, BASE_W = 620
/// Home's items that act on one Mac: they open for the open tab's Mac, or ask which (home-mac-tabs.md › The tab is the Mac).
export const MAC_SCOPED = new Set(['newsession', 'newwindow', 'macfiles', 'macapps', 'promptflow', 'permissions', 'board'])

export class HomeWindow {
  constructor(actions) {
    this.a = actions
    this.root = document.createElement('div')
    this.root.className = 'vwin home'
    this.root.style.width = '620px'
    this.root.style.height = '760px'
    this.state = null
    this.menu = null          // 'more' and 'mac:<key>' (action grids, grid.js) | 'files' | 'layout' | 'route:<key>' | 'sort' | 'row:<itemKey>'
    this.menuAt = null        // where the open menu sits: {right, top} or {left, top}
    this.selecting = null     // {key, ids: Set} — select mode is per Mac, as on the headset
    this.expanded = new Set() // "<mac>|<list>": lists showing every row
    this.codeOpen = new Set() // "<mac>|<item>": a card whose long command is shown whole (needs.js codeBlock)
    this.answered = new Map() // "<mac>|<item>" → when: a card answered from here, shown as sent until the Mac takes it down
                              // (Josh 10-14, phone: "I have to press the allow button like 10 times")
    this.query = ''
    this.filters = new Set(store.get('home.filters', []))
    this.pins = new Set(store.get(PINS, []))
    this.tab = store.get(TAB, null)
    this.width = BASE_W
    this.layoutPick = store.get(LAYOUT, 'tabs') === 'columns' ? 'columns' : 'tabs'   // Tabs or Columns (Josh, 09-29)
    this.confirm = null       // {key, ext, action, row}: an extension action that asks first
    this.backgroundOpen = false
    this.folds = new Map()    // "<mac>|claude:<group>" → folded: a Claude group folded or opened here (the app's fold until then)
    this.root.addEventListener('click', (e) => this.onClick(e))
    this.root.addEventListener('input', (e) => {
      // an Allow until… time being picked (needs.js): kept, so a render meanwhile doesn't reset it
      if (e.target?.classList?.contains('ht-untiltime')) { this.untilValue = e.target.value; return }
      if (e.target?.dataset?.key !== 'hsearch') return
      this.query = e.target.value
      this.render(this.state)
    })
    this.root.addEventListener('mousedown', (e) => this.onDown(e))
    // the hold timer ends with the finger or the mouse — pointerup covers touch, where no mouseup may ever come
    // (Josh 10-14, phone: every tap turned into the row's hold, the next tap swallowed)
    for (const ev of ['mouseup', 'pointerup', 'pointercancel', 'touchend', 'touchcancel']) this.root.addEventListener(ev, () => clearTimeout(this.holdTimer))
    onIcons(() => { if (this.state) this.render(this.state) })
    stylesReady.then(() => { if (this.state) this.render(this.state) })
    gridStyles.then(() => { if (this.state) this.render(this.state) })
    onAskChange(() => { if (this.state) this.render(this.state) })   // a Claude ask answered from the strip (needs.js)
    this.countdownTimer = null   // the once-a-second tick, only while a timed card is on screen (syncCountdown)
  }

  /// A card's time left (Josh 10-14): each second its border and "N min left" move in place, Home is not re-rendered.
  /// The tick runs only while a card with `expires` is drawn, and never holds Node's event loop open (tests).
  syncCountdown() {
    const timed = !!this.root.querySelector?.('.ht-timed[data-expires]')
    if (timed && !this.countdownTimer) {
      this.countdownTimer = setInterval(() => tickCountdowns(this.root), 1000)
      this.countdownTimer?.unref?.()
    } else if (!timed && this.countdownTimer) {
      clearInterval(this.countdownTimer)
      this.countdownTimer = null
    }
  }

  /// What an item needs (core/home-items.js): a paired Mac, or one running the dev Mac app (To Test).
  /// Widget offers on any Mac that nobody looked at in Widgets yet (the Macs' cards carry them; widgets/offers.js).
  newOffers() { return newOffers(this.macs().map((m) => m.cards), this.settings) }
  allowed(needs) { return !needs || (needs === 'paired' && !!this.state?.paired) || (needs === 'testing' && this.macs().some((m) => m.testing && !m.paused)) }
  macs() { return this.state?.macs || [] }
  macOf(key) { return this.macs().find((m) => m.key === key) || null }
  get settings() { return this.a.settings }
  /// The Mac whose tab is open: the one picked last, else the current one, else the first.
  get current() { return this.macOf(this.tab) || this.macOf(this.state?.current) || this.macs()[0] || null }
  /// Where a Mac-scoped action goes without asking: the open tab's Mac (with one Mac, that one) while it isn't
  /// paused; none in Columns (every Mac on screen) — then the action asks.
  get scope() { if (this.layout === 'columns') return null; const m = this.current; return m && !m.paused ? m : null }
  /// A Mac chosen somewhere else (a window's Mac picker, a Mac's ⋯): its tab becomes the open one.
  follow(key) {
    if (!this.macOf(key) || this.tab === key) return
    this.tab = key; store.set(TAB, key); this.selecting = null; this.confirm = null
    if (this.state) this.render(this.state)
  }
  /// The Macs a picker offers: not paused; the last used first, then those online, then the rest in Home's order.
  pickable() {
    const cur = this.state?.current, rank = (m) => (m.key === cur ? 0 : m.link === 'online' ? 1 : 2)
    return this.macs().filter((m) => !m.paused).map((m, i) => [m, i]).sort((a, b) => rank(a[0]) - rank(b[0]) || a[1] - b[1]).map(([m]) => m)
  }
  macHex(m) { return m.color || macColor(m)?.hex || '#8e8e93' }
  /// New Session out front, naming the Mac it opens on — its color and name, kept on a phone where the other
  /// labels go; with no Mac in scope (Columns, a paused tab) it says it will ask.
  newSessionButton() {
    const m = this.scope, several = this.macs().length > 1, off = !this.allowed('paired')
    const title = !several || off ? 'New Session' : m ? `New Session on ${m.name}` : 'New Session — choose a Mac'
    const on = several && m && !off ? `<span class="ht-on"><span class="ht-ondot" style="background:${esc(this.macHex(m))}"></span><span class="ht-onname">${esc(m.name)}</span></span>` : ''
    return `<button class="ht-lb ns" data-act="open:newsession" title="${esc(title)}" aria-label="${esc(title)}" ${off ? 'disabled' : ''}><img src="${icon('plus.bubble')}"><span class="ht-lbl">New Session</span>${on}</button>`
  }
  /// A Mac-scoped item: for the Mac in scope, else the only one awake, else a menu of them (never a silent pick).
  forMac(id, el) {
    const awake = this.pickable(), m = this.scope || (awake.length === 1 ? awake[0] : null)
    if (m || !awake.length) { this.a.open(id, m?.key ?? null); return }
    this.menu = 'pick:' + id
    if (el?.isConnected) this.placeMenu(el); else this.menuAt = null
    this.render(this.state)
  }

  render(state) {
    this.state = state
    const kept = Object.fromEntries([...this.root.querySelectorAll('[data-scroll]')].map((el) => [el.dataset.scroll, [el.scrollTop, el.scrollLeft]]))
    const focused = document.activeElement && this.root.contains(document.activeElement) ? document.activeElement : null
    const fkey = focused?.dataset?.key, fsel = focused?.selectionStart
    if (this.selecting && !this.macOf(this.selecting.key)) this.selecting = null
    // out front: New Session and More (every other tool and Sort, named); the Mac's controls are on the row below
    const nav = `<div class="navbar"><div class="navtitle">Cody McCodeface</div>` +
      this.newSessionButton() +
      labeled('menu:more', 'More', 'ellipsis.circle', { cls: 'more', title: 'More: Input Tools, Widgets, Mac Windows, Files on a Mac, Sort, Layout and the rest', pill: this.newOffers() }) + `</div>`
    const body = state.paired && this.macs().length ? this.macView(state) : this.empty()
    this.root.innerHTML = nav + body + this.popover() + archivedSheet(this)
    for (const el of this.root.querySelectorAll('[data-scroll]')) { const k = kept[el.dataset.scroll]; if (k) { el.scrollTop = k[0]; el.scrollLeft = k[1] } }
    this.fitWidth()
    this.syncCountdown()
    if (fkey) { const again = this.root.querySelector(`[data-key="${fkey}"]`); if (again) { try { again.focus({ preventScroll: true }); if (fsel != null) again.setSelectionRange(fsel, fsel) } catch {} } }
  }

  empty() {
    // inside Cody's Android app: its own Add a Mac (Scan a code, or type it) — no link to paste, no browser to pair
    if (phoneBridge()) return `<div class="unavailable"><img src="${icon('macbook.and.visionpro')}"><div class="h">No server yet</div>` +
      `<div class="d">On the Mac, in Cody: Devices › Pair a device › Phone, then scan its code here.</div>` +
      `<button class="go" data-act="addmac:">Add a Mac</button></div>`
    return `<div class="unavailable"><img src="${icon('macbook.and.visionpro')}"><div class="h">No server yet</div>` +
      `<div class="d">On the Mac, in Cody: Devices › Pair a device, then scan the code.</div>` +
      `<button class="go" data-act="open:addserver">Add Server</button></div>`
  }

  // MARK: Tabs or Columns

  /// Columns only with more than one Mac and room for them: a phone-width screen keeps Tabs.
  get layout() {
    let narrow = false
    try { narrow = !!globalThis.matchMedia?.('(max-width: 700px)').matches } catch {}
    return this.layoutPick === 'columns' && this.macs().length > 1 && !narrow ? 'columns' : 'tabs'
  }
  /// Columns widen Home to show up to three Macs side by side; past that the columns scroll sideways.
  fitWidth() {
    const cols = this.layout === 'columns' && this.state?.paired ? Math.min(3, this.macs().length) : 0
    const w = cols ? cols * (COL_W + COL_GAP) - COL_GAP + 48 : BASE_W
    if (this.width === w) return
    this.width = w
    this.root.style.width = w + 'px'
    this.a.resize?.(w, 760)
  }

  // MARK: the tab strip and the Mac's toolbar

  /// One tab per Mac; with one Mac, just its name.
  tabs() {
    const macs = this.macs(), cur = this.current
    if (macs.length === 1) {
      const m = macs[0]
      return `<div class="ht-one">${this.dot(m)}<span class="ht-name">${esc(m.name)}${this.macBar(m)}</span>${this.routeChip(m)}${this.updateBadge(m)}</div>`
    }
    return `<div class="ht-tabs" data-scroll="tabs">${macs.map((m) => {
      const n = this.needsCount(m)
      const dim = m.paused || m.link === 'offline' || m.link === 'unauthorized'
      return `<button class="ht-tab${m.key === cur?.key ? ' on' : ''}${dim ? ' dim' : ''}" data-act="tab:${esc(m.key)}" title="${esc(this.stateWords(m))}">${this.dot(m)}<span>${esc(m.name)}</span>` +
        (n ? `<span class="ht-count">${n}</span>` : '') + this.macBar(m) + `</button>`
    }).join('')}</div>`
  }
  /// The Mac's own color (tiny-engineer/maccolors.js): the one its robots' laptops wear, under its name.
  macBar(m) { const c = m.color || macColor(m)?.hex; return c ? `<span class="ht-macbar" style="background:${esc(c)}"></span>` : '' }
  dot(m) { return `<span class="ht-dot" style="background:${DOT[m.paused ? 'paused' : m.link] || DOT.paused}"></span>` }
  stateWords(m) { return m.paused ? 'Paused' : m.link === 'online' ? 'Online' : m.link === 'offline' ? 'Offline' : m.link === 'unauthorized' ? 'Signed out' : 'Connecting…' }

  /// Search, Layout, Select and the Mac's ⋯ (named after it), each labeled; then the filter chips. Sort is in More.
  toolbar(m, kinds) {
    const search = `<div class="ht-search"><img src="${icon('magnifyingglass', 'rgb(var(--c-ink-rgb) / 0.55)')}"><input class="field plain" data-key="hsearch" value="${esc(this.query)}" ` +
      `placeholder="Search ${esc(m ? m.name : 'every Mac')}" aria-label="Search — titles, folders, prompts" title="Search — titles, folders, prompts" autocomplete="off" autocapitalize="off" spellcheck="false"></div>`
    const layout = this.macs().length > 1 ? labeled('menu:layout', this.layout === 'columns' ? 'Columns' : 'Tabs', this.layout === 'columns' ? 'rectangle.split.3x1' : 'rectangle.stack', { title: 'Layout: a tab per Mac, or the Macs side by side' }) : ''
    const bar = `<div class="ht-bar">${search}${layout}` +
      (m ? labeled('select:' + m.key, this.selecting?.key === m.key ? 'Done' : 'Select', 'checkmark.circle', { disabled: !m.sessions?.length, title: 'Select sessions to delete' }) +
        labeled('menu:mac:' + m.key, m.name, 'desktopcomputer', { cls: 'mac', title: `${m.name}: Camera, Update, Usage, Robots, Links Open On, Pause, Forget` }) : '') + `</div>`
    const chip = (f, label) => `<button class="ht-chip${this.filters.has(f) ? ' on' : ''}" data-act="filter:${esc(f)}">${esc(label)}</button>`
    const chips = `<div class="ht-chips" data-scroll="chips">${kinds.map(([k, label]) => chip('kind:' + k, label)).join('')}${STATUS_CHIPS.map(([k, label]) => chip('status:' + k, label)).join('')}</div>`
    return bar + chips
  }
  get sort() { return this.settings?.get?.('home.sort', 'recent') || 'recent' }
  /// Group: By folder (an extension's rows by their own group) · Claude groups (the Claude app's sidebar groups, claude-groups.js).
  get grouping() { return this.settings?.get?.('home.group', 'folder') === 'claude' ? 'claude' : 'folder' }

  // MARK: the Mac's view

  macView(s) {
    if (this.layout === 'columns') return this.columns(s)
    const m = this.current
    const items = this.items(m)
    const kinds = [...new Map(items.map((it) => [it.kind, it.kindLabel])).entries()]
    const head = `<div class="ht-head">${this.tabs()}${this.toolbar(m, kinds)}</div>`
    return head + `<div class="scroll" data-scroll="home" style="overflow-y:auto;top:${this.headHeight()}px">${this.overCap(s) + this.notices(m) + this.macLists(m, items)}</div>`
  }

  /// Every Mac side by side: one column each (header, then the same lists as its tab), each scrolling on its own.
  columns(s) {
    const all = this.macs().map((m) => [m, this.items(m)])
    const kinds = [...new Map(all.flatMap(([, items]) => items.map((it) => [it.kind, it.kindLabel]))).entries()]
    const head = `<div class="ht-head">${this.toolbar(null, kinds)}</div>`
    const cols = all.map(([m, items]) => {
      const off = m.paused || m.link === 'offline' || m.link === 'unauthorized'
      const n = items.filter((it) => it.status === 'waiting').length
      const colour = m.color || macColor(m)?.hex
      const header = `<div class="hc-head">${colour ? `<span class="hc-color" style="background:${esc(colour)}"></span>` : ''}${this.dot(m)}<span class="hc-name">${esc(m.name)}</span>` +
        (n ? `<span class="ht-count">${n}</span>` : '') + `<span class="hc-state">${off ? esc(this.stateWords(m)) : ''}</span>` +
        (m.paused ? `<button class="go" data-act="resume:${esc(m.key)}">Resume</button>` : '') +
        `<button class="menu-btn" data-act="menu:mac:${esc(m.key)}" title="${esc(m.name)}"><img src="${icon('ellipsis.circle')}"></button></div>`
      const body = off ? '' : `<div class="hc-body" data-scroll="col-${esc(m.key)}">${this.notices(m)}${this.macLists(m, items)}</div>`
      return `<div class="hc-col${off ? ' dim' : ''}" style="flex:1 1 ${COL_W}px;min-width:${COL_W}px">${header}${body}</div>`
    }).join('')
    return head + `<div class="hc-wrap" data-scroll="cols" style="top:${76 + 52 + 44}px">${this.overCap(s)}<div class="hc-cols">${cols}</div></div>`
  }

  /// A Mac's lists, whichever layout: Needs you, Pinned, by kind, Background.
  macLists(m, items) {
    let out = ''
    if (!m.paused) {
      const shown = this.filtered(items, new Set(m.openHere || []))
      out += this.extNotices(m) + this.needsStrip(m, items)
      const pinned = shown.filter((it) => this.pins.has(this.pinKey(m, it)))
      if (pinned.length) out += this.list(m, 'pinned', 'Pinned', 'pin', pinned)
      // Group › Claude groups: an extension's rows under the Claude app's groups, in its order, Other last (claude-groups.js)
      const claude = this.grouping === 'claude'
      // archived sessions (a Handoff's old one, read-only): out of the lists — the Archived button's list (archived.js)
      const { listed } = archivedApart(shown)
      const groups = new Map()
      for (const it of listed) {
        const id = claude && it.ext ? 'ext:' + it.ext : it.group
        if (!groups.has(id)) groups.set(id, { title: it.groupTitle, symbol: it.groupSymbol, items: [], ext: it.ext })
        groups.get(id).items.push(it)
      }
      // by kind, in the headset's order: Sessions, then each extension's
      const rank = (id) => (id === 'sessions' ? 0 : 1)
      for (const [id, g] of [...groups].sort((a, b) => rank(a[0]) - rank(b[0]))) {
        if (!claude || !g.ext) { out += this.list(m, id, g.title, g.symbol, g.items); continue }
        const extName = (m.ext || []).find((x) => x.ext === g.ext)?.extName || 'Extensions'
        for (const s of claudeSections(g.items, (m.extGroups || []).filter((x) => x.ext === g.ext), extName)) {
          const fk = m.key + '|' + s.id
          out += this.list(m, s.id, s.title, g.symbol, s.items, { key: fk, folded: this.folds.has(fk) ? this.folds.get(fk) : s.collapsed })
        }
      }
      if (!shown.length) out += `<div class="group"><div class="row quiet"><div class="texts"><div class="t">${esc(items.length ? 'Nothing matches — try fewer chips or other words.' : m.link === 'online' ? 'No sessions yet — New Session starts one.' : '…')}</div></div></div></div>`
      out += this.background(m)
      out += archivedButton(this, m)   // "Archived · 3": the list to open, reactivate, delete or clear (archived.js)
    }
    return out
  }
  headHeight() { return 76 + (this.macs().length === 1 ? 40 : 52) + 52 + 44 }

  /// The Mac's own notices: the link (offline, connecting, signed out), the relay card, its schedule, paused.
  notices(m) {
    if (m.paused) return `<div class="ht-notice"><img src="${icon('lucide:circle-pause')}"><div class="texts"><div class="t">${esc(m.name)} is paused</div><div class="c">Nothing connects to it until you resume it.</div></div><button class="go" data-act="resume:${esc(m.key)}">Resume</button></div>`
    let out = ''
    if (m.relay && m.link !== 'online') out += `<div class="ht-notice warn"><img src="${icon('wifi.exclamationmark', 'var(--c-orange)')}"><div class="texts"><div class="t">needs Cody’s relay</div></div></div>`
    else if (m.link === 'offline') out += `<div class="ht-notice warn"><img src="${icon('wifi.exclamationmark', 'var(--c-orange)')}"><div class="texts"><div class="t">Offline</div><div class="c">${esc(m.why || 'The Mac isn’t answering.')}</div></div></div>`
    else if (m.link === 'connecting') out += `<div class="ht-notice"><div class="texts"><div class="t">Connecting to ${esc(m.name)}…</div></div></div>`
    else if (m.link === 'unauthorized') out += `<div class="ht-notice bad"><img src="${icon('lock.trianglebadge.exclamationmark', 'var(--c-red)')}"><div class="texts"><div class="t">Signed out</div><div class="c">Pair this browser with ${esc(m.name)} again.</div></div></div>`
    // offline-first (offline/): the list below is the one kept from the last link — "Offline · as of 14:05"
    if (m.offline) out += `<div class="ht-state">${esc(m.offline)}</div>`
    // the Mac's state, one line under its name (core/macstate.js): "Headless · lid closed · locked · 56% · held until unlock"
    const state = m.link === 'online' ? stateLine(m.macState) : ''
    if (state) out += `<div class="ht-state">${esc(state)}</div>`
    // its MCP servers that need you (mcpcards.js): "linear needs you to sign in" · Sign in on the Mac
    out += mcpCards(m, esc, icon)
    if (this.macs().length > 1 && (this.routeChip(m) || this.updateBadge(m))) out += `<div class="ht-route">${this.routeChip(m)}${this.updateBadge(m)}${m.route ? `<span>${esc(m.route)}${m.relayOnly ? ' · relay only' : ''}</span>` : ''}</div>`
    // a list the Mac refused: why, and the button that fixes it (server-errors.js) — not a bare "400"
    for (const p of m.problems || []) if (p.subject === 'sessions') out += problemCard(p, { act: (a) => `problem:${a}|${m.key}`, esc, icon })
    return out + this.relay(m) + this.schedule(m)
  }

  /// Every row on this Mac, whatever its kind: {key, kind, kindLabel, group, groupTitle, groupSymbol, id, title,
  /// where, time, status, cost, draw(), tap}. Built in one pass per render.
  items(m) {
    const out = []
    const cost = new Map((m.usage?.rows || []).map((r) => [r.sessionID || r.id, r.totals?.cost ?? null]))
    const extKeys = new Set((m.ext || []).map((x) => x.row.session).filter(Boolean))
    for (const x of m.sessions || []) {
      if (extKeys.has(x.id)) continue
      const st = m.waiting?.(x.id) ? 'waiting' : (m.status?.(x.id) || 'idle')
      out.push({ key: `s|${x.id}`, kind: 'opencode', kindLabel: 'opencode', group: 'sessions', groupTitle: 'Sessions', groupSymbol: 'bubble.left.and.text.bubble.right',
        id: x.id, title: displayTitle(x.title), where: tilde(x.directory), time: x.updatedAt || 0, status: st, cost: cost.get(x.id) ?? null, session: x, selectable: true })
    }
    for (const { ext, extName, row } of m.ext || []) {
      // a Claude conversation no device can answer (view only, stale, idle at its prompt) keeps its badge, never waiting
      const st = isClaudeRow(row) && row.status === 'needsYou' && !claudeWaits(row, this.now ? this.now() : Date.now()) ? 'idle'
        : { live: 'live', needsYou: 'waiting', idle: 'idle', ended: 'ended' }[row.status] || 'idle'
      const t = typeof row.time === 'number' ? (row.time < 1e12 ? row.time * 1000 : row.time) : Date.parse(row.time || '') || 0
      // its kind chip: the row's `kind` (one chip per kind the Mac's extensions list), else the extension's name
      const kind = String(row.kind || extName)
      out.push({ key: `x|${ext}|${row.id}`, kind: 'ext:' + ext + '|' + kind, kindLabel: kind, badge: row.badge || kind, group: 'ext:' + ext + (row.group ? '|' + row.group : ''), groupTitle: row.group ? `${extName} · ${row.group}` : extName, groupSymbol: row.symbol || 'puzzlepiece.extension',
        id: String(row.id), title: displayTitle(row.title), where: row.subtitle || '', time: t, status: st, cost: typeof row.cost === 'number' ? row.cost : null, costText: typeof row.cost === 'string' ? row.cost : null, ext, row })
    }
    return out
  }

  /// Search, then the chips: one sort any-of, different sorts all-of; then the order picked (Recent: what is open
  /// here first, the one used last on top, then the rest by activity — order.js). `openHere`: session ids with a
  /// window open on this device.
  filtered(items, openHere = new Set()) {
    const q = this.query.trim().toLowerCase()
    const kinds = [...this.filters].filter((f) => f.startsWith('kind:')).map((f) => f.slice(5))
    const statuses = [...this.filters].filter((f) => f.startsWith('status:')).map((f) => f.slice(7))
    const out = items.filter((it) => (!q || [it.title, it.where, it.prompt].some((f) => (f || '').toLowerCase().includes(q))) &&
      (!kinds.length || kinds.includes(it.kind)) && (!statuses.length || statuses.includes(it.status)))
    const rank = { waiting: 0, working: 1, live: 2 }
    const sort = this.sort
    if (sort !== 'cost' && sort !== 'needs') return orderRecent(out, openHere)
    return out.sort((a, b) => sort === 'cost' ? (b.cost ?? -1) - (a.cost ?? -1) || b.time - a.time
      : (rank[a.status] ?? 3) - (rank[b.status] ?? 3) || b.time - a.time)
  }

  needsCount(m) { return this.items(m).filter((it) => it.status === 'waiting').length }

  /// Everything waiting on the person, in orange, at the top; tap opens it — a Claude ask with its buttons (needs.js).
  needsStrip(m, items) { return needsStrip(this, m, items) }

  /// A list: its header, its first rows (all of them while searching or expanded), Show all N. `fold` ({key, folded}):
  /// a Claude group — its header folds and opens it (the app's fold at first), a folded one shows the header alone.
  list(m, id, title, symbol, items, fold = null) {
    const ek = m.key + '|' + id
    const head = fold
      ? `<div class="cchead fold" data-act="fold:${fold.folded ? 1 : 0}|${esc(fold.key)}" role="button" aria-expanded="${fold.folded ? 'false' : 'true'}" aria-label="${esc(title)}"><img src="${icon(fold.folded ? 'chevron.right' : 'chevron.down', 'rgb(var(--c-ink-rgb) / 0.55)')}">${esc(title)}<span class="ht-n">${items.length}</span></div>`
      : `<div class="cchead"><img src="${icon(symbol, 'rgb(var(--c-ink-rgb) / 0.55)')}">${esc(title)}<span class="ht-n">${items.length}</span></div>`
    if (fold?.folded) return head
    const all = this.query || this.expanded.has(ek) || (this.selecting?.key === m.key && id === 'sessions')
    const shown = all ? items : items.slice(0, FIRST_ROWS)
    let rows = shown.map((it, i) => (i ? '<div class="sep"></div>' : '') + this.row(m, it)).join('')
    if (items.length > FIRST_ROWS && !this.query) {
      const open = this.expanded.has(ek)
      rows += `<div class="sep"></div><div class="row quiet" data-act="expand:${esc(ek)}"><img class="check" src="${icon(open ? 'chevron.up' : 'chevron.down', 'rgb(var(--c-ink-rgb) / 0.55)')}">` +
        `<div class="texts"><div class="t">${open ? 'Show fewer' : `Show all ${items.length}`}</div></div></div>`
    }
    if (id === 'sessions' && this.selecting?.key === m.key) rows += this.selbar(m)
    return `${head}<div class="group">${rows}</div>`
  }

  /// One row, by kind.
  row(m, it) {
    const sel = this.selecting?.key === m.key && it.selectable ? this.selecting.ids : null
    const lead = sel
      ? `<img class="check" src="${sel.has(it.id) ? icon('checkmark.circle.fill', 'var(--c-blue)') : icon('circle', 'rgb(var(--c-ink-rgb) / 0.55)')}">`
      : it.row?.symbol ? `<img class="check" src="${icon(it.row.symbol, it.row.tint || 'rgb(var(--c-ink-rgb) / 0.7)')}">`
        : `<div class="dot ${it.status === 'working' ? 'working' : it.status === 'waiting' ? 'attention' : it.status === 'live' ? 'live' : ''}"></div>`
    const money = this.costOf(it)
    const primary = it.row?.actions?.find((a) => a.primary)
    // a card's row (no primary action — an approval's Allow once · Deny, a pairing's Trust · Cancel): its buttons sit on
    // the row itself (Josh 10-14, on the phone: "tapping on it does nothing", "holding did nothing")
    const buttons = !primary && it.row?.actions?.length && this.answered.has(m.key + '|' + it.key)
      ? `<div class="ht-cardacts"><span class="ht-sent">Sent</span></div>`
      : !primary && it.row?.actions?.length
      ? `<div class="ht-cardacts">${it.row.actions.slice(0, 3).map((a, i) => `<button class="ht-cardact${/deny|cancel|no\b/i.test(a.title || a.id) ? ' quiet' : ''}" data-act="xact:${esc(m.key)}|${esc(it.key)}|${i}">${esc(a.title || a.id)}</button>`).join('')}</div>`
      : ''
    const trail = primary ? `<div class="ccact${it.status === 'live' ? ' live' : ''}">${esc(primary.title)}</div>` : buttons
    const note = this.confirm?.key === m.key && this.confirm?.item === it.key ? this.confirmCard(m) : ''
    return `<div class="row ht-row" data-act="${esc(this.tapOf(m, it))}" data-row="${esc(m.key)}|${esc(it.key)}">${lead}<div class="texts">` +
      `<div class="t">${esc(it.title)}</div><div class="c ht-cap"><span class="ht-badge">${esc(it.badge || it.kindLabel)}</span>` +
      // a remote agent's command (the Approvals row's `code`): the command in mono, then its folder
      (it.row?.code ? `<code class="ht-code">${esc(it.row.code)}</code><span>${esc([it.row.folder, ago(it.time)].filter(Boolean).join(' · '))}</span>`
        : `<span>${esc([it.where, ago(it.time)].filter(Boolean).join(' · '))}</span>`) +
      (money ? `<span class="ht-cost">${esc(money)}</span>` : '') + `</div></div>${trail}</div>` + note
  }
  costOf(it) { return it.costText || (it.cost != null ? money(it.cost) : '') }
  tapOf(m, it) {
    if (it.ext) return `xrow:${m.key}|${it.key}`
    return `row:${m.key}|${it.id}`
  }
  pinKey(m, it) { return m.key + '|' + it.key }
  /// Select All: this Mac's opencode sessions no extension speaks for (the ones its Sessions list shows).
  selectableIds(m) { const ext = new Set((m?.ext || []).map((x) => x.row.session).filter(Boolean)); return (m?.sessions || []).filter((x) => !ext.has(x.id)).map((x) => x.id) }

  selbar(m) {
    const ids = this.selecting.ids, n = ids.size
    const everything = n === this.selectableIds(m).length
    return `<div class="sep" style="margin-left:0"></div><div class="selbar">` +
      `<button data-act="sel:${everything ? 'none' : 'all'}">${everything ? 'Select None' : 'Select All'}</button>` +
      `<button data-act="sel:cancel">Cancel</button><div class="spacer"></div>` +
      `<button class="danger" data-act="sel:delete" ${n ? '' : 'disabled'}><img src="${icon('trash', 'var(--c-red)')}">Delete ${n} Session${n === 1 ? '' : 's'}</button></div>`
  }

  /// Background, folded, last: this Mac's widgets and saved panes (the Mac Widgets panel has them all).
  background(m) {
    const widgets = (m.cards?.cards || []).filter((c) => c.kind === 'skill')
    const saved = m.saved || []
    if (!widgets.length && !saved.length) return ''
    const open = this.backgroundOpen
    let rows = `<div class="row" data-act="background:"><img class="check" src="${icon(open ? 'chevron.down' : 'chevron.right', 'rgb(var(--c-ink-rgb) / 0.55)')}"><div class="texts"><div class="t">Background</div>` +
      `<div class="c">${esc([widgets.length && `${widgets.length} widget${widgets.length === 1 ? '' : 's'}`, saved.length && `${saved.length} saved pane${saved.length === 1 ? '' : 's'}`].filter(Boolean).join(' · '))}</div></div></div>`
    if (open) {
      rows += [...widgets.map((w) => ({ t: w.title || 'Widget', c: w.value != null ? String(w.value) : w.label || '' })), ...saved.map((p) => ({ t: p.title || 'Pane', c: 'saved pane' }))]
        .map((r) => `<div class="sep"></div><div class="row" data-act="macpanel:macwidgets|${esc(m.key)}"><div class="dot"></div><div class="texts"><div class="t">${esc(r.t)}</div><div class="c">${esc(r.c)}</div></div></div>`).join('')
    }
    return `<div class="group ht-bg">${rows}</div>`
  }

  /// Past the cap nothing is dropped by itself: Home asks which Mac to forget to keep the one just paired.
  overCap(s) {
    if (!s.overCap) return ''
    const older = this.macs().filter((m) => m.key !== s.overCap.newest && !m.tunnel)
    const rows = older.map((m, i) => (i ? '<div class="sep"></div>' : '') +
      `<div class="row" data-act="forget:${esc(m.key)}"><img class="check" src="${icon('xmark.circle', 'var(--c-red)')}"><div class="texts">` +
      `<div class="t">Forget ${esc(m.name)}</div><div class="c">${esc(m.paused ? 'paused' : m.link === 'online' ? 'online' : m.link === 'offline' ? 'offline' : 'connecting…')}</div></div></div>`).join('')
    return `<div class="sechead"><img class="big" src="${icon('exclamationmark.triangle', 'var(--c-orange)')}"><div class="names"><div class="name">Too many Macs</div>` +
      `<div class="sub">This browser keeps ${s.overCap.cap}. Forget one to keep the Mac you just paired.</div></div></div><div class="group">${rows}</div>`
  }

  /// How this Mac's bytes travel, at a glance (Josh, 09-29: "I don't know if I'm using the relay"): Relay (orange)
  /// or Direct (green) beside the name; tapped, a popover with the route, the candidate types and when it was seen.
  routeChip(m) {
    const r = m.routeInfo
    if (m.paused || m.link !== 'online' || !r) return ''
    const relay = m.relayStatus ? m.relayStatus.route === 'relay' : r.kind === 'relay'
    return `<button class="routechip ${relay ? 'relay' : 'direct'}" data-act="menu:route:${esc(m.key)}" title="${esc(r.label)}">${routeIcon(relay ? 'relay' : 'direct')}${esc(chipText(m.relayStatus, r, m.name))}</button>`
  }

  /// A newer Cody McCodeface on this Mac's feed (MacUpdateBadge): tapped, the Update Mac App panel.
  updateBadge(m) {
    const u = m.update
    if (m.paused || m.link !== 'online' || !u?.available || u.phase === 'current' || u.available === u.version) return ''
    return `<button class="routechip upd" data-act="macpanel:macupdate|${esc(m.key)}" title="${esc(`Cody McCodeface ${u.available} is available for this Mac — ⋯ › Update Mac App…`)}">↓ ${esc(u.available)}</button>`
  }

  /// Cody's relay was needed and refused: a card saying what to do, with the button that does it (relaycard.js).
  relay(m) {
    if (m.paused || m.link === 'online') return ''
    const card = relayCard(m.relay)
    if (!card) return ''
    const button = card.action ? `<button class="go" data-act="relay:${card.action.act}|${esc(m.key)}">${esc(card.action.label)}</button>` : ''
    return `<div class="relaycard"><img src="${icon(card.symbol, 'var(--c-orange)')}"><div class="texts"><div class="t">${esc(card.title)}</div>` +
      `${card.detail ? `<div class="c">${esc(card.detail)}</div>` : ''}</div>${button}</div>`
  }

  /// The Mac's workspace is off by its schedule, or about to be (schedulecard.js): the words, and Start Now.
  schedule(m) {
    if (m.paused) return ''
    const card = scheduleCard(m.schedule, { allowed: m.scheduleAllowed, asked: m.scheduleAsked, refused: m.scheduleRefused })
    if (!card) return ''
    const button = card.action ? `<button class="go" data-act="startnow:${esc(m.key)}" ${card.action.act ? '' : 'disabled'}>${esc(card.action.label)}</button>` : ''
    return `<div class="relaycard" style="background:rgb(var(--c-ink-rgb) / 0.08)"><img src="${icon(card.symbol)}"><div class="texts"><div class="t">${esc(card.title)}</div>` +
      `${card.detail ? `<div class="c">${esc(card.detail)}</div>` : ''}</div>${button}</div>`
  }

  /// An extension action that asks first: its words under the row, Cancel and the action.
  confirmCard(m) {
    const c = this.confirm
    return `<div class="ccnote"><img src="${icon('exclamationmark.triangle', 'var(--c-orange)')}"><div class="texts"><div class="t">${esc(c.action.title || 'Are you sure?')}</div><div class="c">${esc(c.action.confirm)}</div></div>` +
      `<button class="go" data-act="xconfirm:no">Cancel</button><button class="go" data-act="xconfirm:yes">${esc(c.action.title || 'OK')}</button></div>`
  }

  /// The open Menu, as a glass popover under its button.
  popover() {
    const at = this.menuAt ? Object.entries(this.menuAt).map(([k, v]) => `${k}:${v}px`).join(';') : 'right:24px;top:78px'
    const mi = (act, label, symbol, cls = '') => `<button class="mi${cls ? ' ' + cls : ''}" data-act="${esc(act)}">${symbol ? `<img src="${icon(symbol, cls === 'danger' ? 'var(--c-red)' : 'var(--c-ink)')}">` : '<span class="sym"></span>'}${esc(label)}</button>`
    const checked = (act, label, on) => `<button class="mi" data-act="${esc(act)}">${on ? `<img src="${icon('lucide:check')}">` : '<span class="sym"></span>'}${esc(label)}</button>`
    // More and a Mac's ⋯: an action grid — big labeled tiles, sections, chips (grid.js)
    if (this.menu === 'more') return moreGrid(this)
    if (this.menu?.startsWith('mac:')) return macGrid(this, this.menu.slice(4))
    // a Mac-scoped item with no Mac in scope (Columns, a paused tab): which Mac — the last used first, checked;
    // each in its color, its state when it isn't online
    if (this.menu?.startsWith('pick:')) {
      const id = this.menu.slice(5), item = [...homeItems('toolbar'), ...homeItems('more')].find((i) => i.id === id)
      const items = this.pickable().map((m) => `<button class="mi" data-act="pickmac:${esc(id)}|${esc(m.key)}"><span class="ht-ondot pick" style="background:${esc(this.macHex(m))}"></span>${esc(m.name)}` +
        (m.link !== 'online' ? `<span class="mstate">${esc(this.stateWords(m))}</span>` : '') +
        `${m.key === this.state.current ? `<span class="spacer"></span><img src="${icon('lucide:check')}">` : ''}</button>`).join('')
      return `<div class="menu" style="${at}"><div class="msec">${esc((item?.label || id).replace(/…$/, ''))} on which Mac?</div>${items}</div>`
    }
    if (this.menu === 'layout') return `<div class="menu" style="${at}"><div class="msec">Layout</div>${checked('layout:tabs', 'Tabs — one Mac at a time', this.layout === 'tabs')}${checked('layout:columns', 'Columns — every Mac side by side', this.layout === 'columns')}</div>`
    if (this.menu === 'sort') return `<div class="menu" style="${at}"><div class="msec">Sort</div>${SORTS.map(([k, label]) => checked('sort:' + k, label, this.sort === k)).join('')}</div>`
    if (this.menu?.startsWith('route:')) {
      const m = this.macOf(this.menu.slice(6)), r = m?.routeInfo
      if (!r) return ''
      const relay = r.kind === 'relay'
      const kind = (t) => ({ host: 'host (this network)', srflx: 'server-reflexive (through NAT)', prflx: 'peer-reflexive (through NAT)', relay: 'relay (TURN)' }[t] || t || 'unknown')
      const line = (a, b) => `<div class="rline"><span>${esc(a)}</span><span>${esc(b)}</span></div>`
      const measured = r.checkedAt ? (ago(r.checkedAt) === 'now' ? 'just now' : ago(r.checkedAt)) : '—'
      return `<div class="menu routepop" style="${at}"><div class="msec">${esc(m.name)}</div>` +
        `<div class="rhead"><span class="routechip ${relay ? 'relay' : 'direct'}">${routeIcon(relay ? 'relay' : 'direct')}${relay ? 'Relay' : 'Direct'}</span>${esc(r.label)}</div>` +
        line('This browser', kind(r.local)) + line('The Mac', kind(r.remote)) +
        line('Going this way since', r.seenAt ? new Date(r.seenAt).toLocaleTimeString() : '—') +
        line('Last measured', measured) +
        line('Relay only (test)', m.relayOnlySetting ? 'on' : 'off') + relayDetail(m, line) + `</div>`
    }
    if (this.menu?.startsWith('row:')) {
      const [key, ik] = splitRow(this.menu.slice(4)), m = this.macOf(key), it = m && this.items(m).find((x) => x.key === ik)
      if (!it) return ''
      const pinned = this.pins.has(this.pinKey(m, it))
      let rows = mi(this.tapOf(m, it), 'Open', 'arrow.up.forward.square')
      for (const [i, a] of (it.row?.actions || []).entries()) rows += mi(`xact:${m.key}|${it.key}|${i}`, a.title || a.id, a.symbol)
      rows += mi(`pin:${m.key}|${it.key}`, pinned ? 'Unpin' : 'Pin', 'pin')
      if (it.selectable) rows += mi(`selectrow:${m.key}|${it.id}`, 'Select', 'checkmark.circle') + mi(`deleterow:${m.key}|${it.id}`, 'Delete…', 'trash', 'danger')
      rows += `<div class="div"></div>` + mi('pause:' + m.key, `Pause ${m.name}`, 'lucide:pause')
      return `<div class="menu" style="${at}">${rows}</div>`
    }
    return ''
  }

  /// A menu opens under the button that asked for it (or above it, near the bottom).
  placeMenu(el, left = false) {
    try {
      const r = el.getBoundingClientRect(), root = this.root.getBoundingClientRect()
      const below = r.bottom - root.top + 6
      const right = Math.max(12, Math.round(root.right - r.right))
      const top = below < root.height - 220 ? Math.round(below) : Math.max(12, Math.round(r.top - root.top - 220))
      this.menuAt = left ? { left: Math.max(12, Math.round(r.left - root.left + 40)), top } : { right, top }
    } catch { this.menuAt = null }
  }

  onDown(e) {
    const row = e.target.closest?.('[data-row]')
    if (!row || this.selecting) return
    clearTimeout(this.holdTimer)
    // a long press (0.5 s) is the row's menu: Open, Pin, Select, Delete…, an extension's actions
    this.holdTimer = setTimeout(() => {
      this.menu = 'row:' + row.dataset.row
      this.placeMenu(row, true)
      this.suppress = true
      this.render(this.state)
    }, 500)
  }

  onClick(e) {
    const el = e.target.closest?.('[data-act]')
    const act = el?.dataset.act || ''
    if (this.suppress) { this.suppress = false; return }
    if (el?.disabled) return
    if (this.menu && !act.startsWith('menu:')) {
      const was = this.menu
      // an action grid (grid.js): a tile counts its use and the sheet closes; a chip changes its setting and the sheet stays
      const stays = !!el?.closest('.ag-chips')
      if (el?.closest('.ag') && el.dataset.use) bumpUse(gridKind(was), el.dataset.use)
      if (!stays) this.menu = null
      if (!el || !el.closest('.menu, .ag')) { this.render(this.state); return }
      if (was && !stays) this.render(this.state)
    }
    if (!act) return
    const [kind, arg] = [act.slice(0, act.indexOf(':') < 0 ? act.length : act.indexOf(':')), act.slice(act.indexOf(':') + 1)]
    if (kind === 'menu') {
      if (this.menu === arg) this.menu = null
      else { this.menu = arg; this.placeMenu(el) }
      this.render(this.state); return
    }
    if (kind === 'open') {
      // an item that acts on one Mac: the open tab's, else ask which
      if (MAC_SCOPED.has(arg)) { this.forMac(arg, el); return }
      this.a.open(arg); return
    }
    if (kind.startsWith('arch') && archivedTap(this, kind, arg)) return   // the Archived list's (archived.js)
    const [key, rest] = splitRow(arg)
    switch (kind) {
      // the tab is the app's current Mac: every window and action opened from here on goes to it (home-mac-tabs.md › The tab is the Mac)
      case 'tab': this.tab = arg; store.set(TAB, arg); this.selecting = null; this.confirm = null; this.a.chooseMac?.(arg); this.render(this.state); return
      case 'pickmac': this.a.open(key, rest); return   // "pickmac:<item>|<Mac>": an item's id has no bar
      case 'cask': needsTap(this, key, rest); return
      // an agent's Allow until… / Auto until… (Josh 10-17): its chooser, then a quick choice or the time picked there
      case 'xuntil': untilToggle(this, key, rest); return
      case 'xuntilgo': untilSend(this, key, rest, el?.closest?.('.ht-until')?.querySelector?.('input')?.value); return
      case 'macpanel': this.a.open(key, rest); return
      case 'forget': this.a.forgetMac(arg); return
      case 'pause': this.a.pauseMac(arg); return
      case 'resume': this.a.resumeMac(arg); return
      case 'startnow': this.a.startNow?.(arg); return
      case 'problem': this.a.problemAction?.(rest, key); return   // "problem:<action>|<Mac>"
      case 'mcp': { const [action, id] = splitRow(rest); this.a.mcpPress?.(key, id, action); return }
      case 'relay': this.a.relayAction(key, rest); return
      case 'addmac': phoneAddMac(); return   // the phone app's Add a Mac (empty())
      case 'sort': this.settings?.set?.('home.sort', arg); this.render(this.state); return
      case 'group': this.settings?.set?.('home.group', arg === 'claude' ? 'claude' : 'folder'); this.render(this.state); return
      case 'fold': { const at = arg.indexOf('|'); this.folds.set(arg.slice(at + 1), arg.slice(0, at) !== '1'); this.render(this.state); return }
      case 'layout': this.layoutPick = arg === 'columns' ? 'columns' : 'tabs'; store.set(LAYOUT, this.layoutPick); this.render(this.state); return
      case 'linksopen': this.settings?.set?.('links.openOn', arg); this.render(this.state); return
      case 'filter': if (this.filters.has(arg)) this.filters.delete(arg); else this.filters.add(arg); store.set('home.filters', [...this.filters]); this.render(this.state); return
      case 'background': this.backgroundOpen = !this.backgroundOpen; this.render(this.state); return
      case 'expand': if (this.expanded.has(arg)) this.expanded.delete(arg); else this.expanded.add(arg); this.render(this.state); return
      case 'codeopen': if (this.codeOpen.has(arg)) this.codeOpen.delete(arg); else this.codeOpen.add(arg); this.render(this.state); return
      case 'pin': { const k = arg; if (this.pins.has(k)) this.pins.delete(k); else this.pins.add(k); store.set(PINS, [...this.pins]); this.render(this.state); return }
      case 'select': this.selecting = this.selecting?.key === arg ? null : { key: arg, ids: new Set() }; this.render(this.state); return
      case 'selectrow': this.selecting = { key, ids: new Set([rest]) }; this.render(this.state); return
      case 'deleterow': this.selecting = { key, ids: new Set([rest]) }; this.render(this.state); return
      case 'xrow': return this.extTap(key, rest)
      case 'xact': { const [ik, i] = [rest.slice(0, rest.lastIndexOf('|')), +rest.slice(rest.lastIndexOf('|') + 1)]; return this.extAction(key, ik, i) }
      // the card's own button in the Needs you strip: the card was read there, so its answer goes at once
      case 'xcard': { const [ik, i] = [rest.slice(0, rest.lastIndexOf('|')), +rest.slice(rest.lastIndexOf('|') + 1)]; return this.extAction(key, ik, i, true) }
      case 'xconfirm': {
        const c = this.confirm; this.confirm = null
        if (arg === 'yes' && c) this.a.extAction?.(c.key, c.ext, { action: c.action.id, args: c.action.args || {}, target: 'home', opensURL: !!c.action.opensURL })
        this.render(this.state); return
      }
      // an extension's notice: its button, or ✕
      case 'xnote': {
        const [id, what] = rest.split('|'), n = (this.macOf(key)?.extNotices || []).find((x) => String(x.id) === id)
        if (!n) return
        if (what === 'go' && n.action) this.a.extAction?.(key, n.ext, { action: n.action.id, args: n.action.args || {}, target: 'home', opensURL: !!n.action.opensURL })
        this.a.extDismiss?.(key, n.id)
        return
      }
      case 'row': {
        if (!this.selecting) { const s = this.macOf(key)?.sessions.find((x) => x.id === rest); if (s) this.a.openSession(s, key); return }
        if (this.selecting.key !== key) return   // select mode is one Mac's list
        const ids = this.selecting.ids
        if (ids.has(rest)) ids.delete(rest); else ids.add(rest)
        this.render(this.state); return
      }
      case 'sel': {
        if (!this.selecting) return
        const { key: k, ids } = this.selecting
        if (arg === 'all') this.selecting = { key: k, ids: new Set(this.selectableIds(this.macOf(k))) }
        else if (arg === 'none') this.selecting = { key: k, ids: new Set() }
        else if (arg === 'cancel') this.selecting = null
        else if (arg === 'delete' && ids.size) { this.selecting = null; this.a.deleteSessions([...ids].map((id) => ({ key: k, id }))) }
        this.render(this.state); return
      }
      default:
    }
  }

  /// An extension's row: its primary action, else the session it stands for.
  extTap(key, ik) {
    const m = this.macOf(key), it = m && this.items(m).find((x) => x.key === ik)
    if (!it) return
    // its Mac out of reach: its session window over what this device kept (offline/) — its line in the glance is the row's id
    if (m.link !== 'online') return this.a.openSession({ id: it.row.session || String(it.row.id), title: it.title }, key)
    const primary = it.row.actions?.findIndex((a) => a.primary)
    if (primary >= 0) return this.extAction(key, ik, primary)
    if (it.row.session) { const s = m.sessions?.find((x) => x.id === it.row.session) || { id: it.row.session }; this.a.openSession(s, key) }
  }
  extAction(key, ik, i, direct = false) {
    const m = this.macOf(key), it = m && this.items(m).find((x) => x.key === ik), a = it?.row?.actions?.[i]
    if (!a) return
    if (a.confirm && !direct) { this.confirm = { key, item: ik, ext: it.ext, action: a }; this.render(this.state); return }
    if (direct || !it.row.actions.some((x) => x.primary)) { this.answered.set(key + '|' + ik, Date.now()); setTimeout(() => { if (this.answered.delete(key + '|' + ik) && this.state) this.render(this.state) }, 30000) }
    // a device in the Mac's meeting (pairing from anywhere, pair-anywhere.md): Pair · Not now go as meetAnswer {code, pair}
    if (it.row.meet?.code) this.a.meetAnswer?.(key, it.row.meet.code, a.id === 'approve')
    else this.a.extAction?.(key, it.ext, { action: a.id, args: a.args || {}, target: 'home', opensURL: !!a.opensURL })
    if (this.answered.has(key + '|' + ik) && this.state) this.render(this.state)   // the card reads Sent at once
  }

  /// What the Mac's extensions said with `notify`, newest first, until dismissed.
  extNotices(m) {
    return (m.extNotices || []).slice().reverse().map((n) => `<div class="ht-notice"><img src="${icon('bell')}"><div class="texts"><div class="t">${esc(n.title)}</div>` +
      (n.body ? `<div class="c">${esc(n.body)}</div>` : '') + `</div>` +
      (n.action ? `<button class="go" data-act="xnote:${esc(m.key)}|${n.id}|go">${esc(n.action.title || n.action.id || 'Open')}</button>` : '') +
      `<button class="go" data-act="xnote:${esc(m.key)}|${n.id}|x" title="Dismiss">✕</button></div>`).join('')
  }
}

/// A "key|rest" pair: the room keys are hex, so the first bar splits them.
function splitRow(value) { const at = value.indexOf('|'); return at < 0 ? [value, ''] : [value.slice(0, at), value.slice(at + 1)] }
